/* @flow */
import * as React from 'react';
import { debounce } from 'lodash';
import { WithRouterProps } from 'react-router';

import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import CurrentUser from 'auth/CurrentUser';
import Dialog from 'components/Dialog';
import Divider from 'components/Divider';
import errorLogger from 'utils/errorLogger';
import ErrorReporter from 'app/components/ErrorReporter';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupAvatarSelector from './GroupAvatarSelector';
import GroupForm from './GroupForm';
import GroupOptionsForm from './GroupOptionsForm';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import ToastActions from 'actions/ToastActions';
import ConfirmDialog from 'components/ConfirmDialog';
import SETTINGS_ROUTES from 'settings/constants/SettingsRouteConstants';
import { withRouter } from 'app/context/Router';

// @ts-ignore
import { UNAFFILIATED_CLASS_ID } from 'groups/components/GroupSchoolSelect';

import './styles/GroupCreateModal.scss';

export type Props = {
  onCreate?: ?(group: Group) => void,
  onRequestHide: () => void,
} & WithRouterProps;

type State = {
  hasChildren: boolean,
  code: ?string,
  name: ?string,
  selectedOrgId: ?number,
  avatarHandpicked: boolean,
  groupCodeHandpicked: boolean,
  suggestedGroupCode: boolean,
  canSubmit: boolean,
};

class GroupCreateModal extends React.Component<Props, State> {
  state: State = {
    hasChildren: false,
    code: null,
    name: null,
    selectedOrgId: null,
    avatarHandpicked: false,
    groupCodeHandpicked: false,
    suggestedGroupCode: false,
    canSubmit: false,
  };

  avatarSelector: ?React.Element<typeof GroupAvatarSelector> = React.createRef();

  suggestGroupCode = debounce((name: string) => {
    Group.suggestCode(name)
      .then(codes => {
        if (!this.state.code && codes && codes[0]) {
          this.setState({ code: codes[0] });
          this.refs.groupForm.setState({ codeFromField: codes[0] });
        }
        this.setState({ suggestedGroupCode: true });
      })
      .catch(errorLogger('GroupCreateModal suggestCode failed'));
  }, 300);

  suggestAvatarForName = debounce((name: string) => {
    if (!this || !this.avatarSelector || !this.avatarSelector.current) {
      return;
    }
    this.avatarSelector.current.searchAvatarForName(name);
  }, 300);

  handleAvatarSelected = () => {
    this.setState({ avatarHandpicked: true });
  };

  handleCodeChange = () => {
    if (!this.state.groupCodeHandpicked) {
      this.setState({ groupCodeHandpicked: true });
    }
  };

  handleOptionsChange = (name: string, value: any) => {
    this.setState({ [name]: value });
  };

  handleSubmit = () => {
    if (!this || !this.avatarSelector || !this.avatarSelector.current) {
      return;
    }

    const { selectedOrgId } = this.state;
    const orgData = selectedOrgId
      ? {
          organization: { id: selectedOrgId },
        }
      : {};

    const groupToCreate = new Group({
      name: this.state.code,
      class_name: this.state.name,
      group_avatar: {
        id: this.avatarSelector.current.getSelectedAvatarId(),
      },
      ...this.refs.options.getValues(),
      ...orgData,
    });

    GroupActions.create(groupToCreate)
      .then(group => {
        this.props.onRequestHide();
        if (this.props.onCreate) {
          this.props.onCreate(group);
        }
      })
      .catch(ToastActions.showAPIError);
  };

  handleFormInvalid = () => {
    // Prevent excessive re-renders
    if (this.state.canSubmit) {
      this.setState({ name: null, code: null, canSubmit: false });
    }
  };

  handleFormValid = (name: string, code: string, orgId?: ?string) => {
    this.setState({
      name,
      code,
      // TODO -- add a test for this (to avoid future p0s)
      // if the orgId is equal to the UNAFFILIATED_CLASS_ID, then we need to set this key to null,
      // otherwise pass in the org id
      // if the orgId is undefined for some god forsaken reason then use that in the key so that it doesn't get sent to r101-api as null and
      //   remove us from the org
      selectedOrgId:
        orgId === UNAFFILIATED_CLASS_ID ? null : (orgId && parseInt(orgId, 10)) || undefined,
      canSubmit: true,
    });
  };

  handleValidNameChange = (name: string) => {
    if (!this.state.groupCodeHandpicked && !this.state.suggestedGroupCode) {
      this.suggestGroupCode(name);
    }

    if (!this.state.avatarHandpicked) {
      this.suggestAvatarForName(name);
    }
  };

  render() {
    if (!CurrentUser.isSignedIn()) {
      return <ErrorReporter error="GroupCreateModal - user is not signed in" />;
    }

    const user = CurrentUser.getUser();
    if (user && !user.hasEmail()) {
      return (
        <ConfirmDialog
          title="Add an email address to your account first"
          subtext="An email address is required to create a class. You can add one in your account settings."
          actionButtonText="Go to Settings"
          cancelButtonText="Cancel"
          onRequestHide={this.props.onRequestHide}
          onConfirm={() => this.props.router.push(SETTINGS_ROUTES.NOTIFICATIONS)}
          eventKey="email_chat_history.no_email_modal.view"
        />
      );
    }

    return (
      <Dialog
        {...this.props}
        eventScreenName="GroupCreateModal"
        className="GroupCreateModal"
        title="Create a class"
        size="small">
        <BoxBody divided>
          <MediaObject className="GroupCreateModal-top">
            <div>
              <GroupAvatarSelector ref={this.avatarSelector} onSelect={this.handleAvatarSelected} />
            </div>
            <div>
              <GroupForm
                ref="groupForm"
                name={this.state.name || ''}
                code={this.state.code || ''}
                onFormValid={this.handleFormValid}
                onValidNameChange={this.handleValidNameChange}
                onCodeChange={this.handleCodeChange}
                onFormInvalid={this.handleFormInvalid}
                hasChildren={this.state.hasChildren}
                showOrg
              />
            </div>
          </MediaObject>
          <Divider size="medium" />
          <Row>
            <GroupOptionsForm
              ref="options"
              subscriberInitiatedChats
              hasChildren={this.state.hasChildren}
              showSearchable={false}
              userIsTeacher={CurrentUser.getUser().isTeacher()}
              onChange={this.handleOptionsChange}
              showRequestToJoin={false}
            />
          </Row>
          <Button
            className="GroupCreateModal-submitButton"
            disabled={!this.state.canSubmit}
            onClick={this.handleSubmit}
            data-test-selector="group-create-modal-submit">
            Create
          </Button>
        </BoxBody>
      </Dialog>
    );
  }
}

export default withRouter(GroupCreateModal);
